import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid2 as Grid,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Alert,
  Paper,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Checkbox,
  FormControlLabel,
  TextField,
  IconButton,
  useMediaQuery,
  useTheme,
  LinearProgress,
} from "@mui/material";
import {
  Approval,
  CheckCircle,
  Description,
  Send,
  ArrowBack,
  Download,
} from "@mui/icons-material";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";

const PpStampingWorkflowPage = () => {
  const { submissionId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const currentSession = useSelector((state) => state.sessions.currentSession);
  const currentUser = useSelector((state) => state[currentSession]?.user);

  const [submission, setSubmission] = useState(null);
  const [activeStep, setActiveStep] = useState(0);
  const [checkedPages, setCheckedPages] = useState({});
  const [stampingProgress, setStampingProgress] = useState(0);
  const [pcaFormData, setPcaFormData] = useState({
    reviewedBy: "",
    licenseNumber: "",
    sealNumber: "",
    reviewDate: new Date().toISOString().split("T")[0],
    specialConditions: "",
    codeCompliance: true,
  });
  const [stampingComplete, setStampingComplete] = useState(false);
  const [pcaGenerated, setPcaGenerated] = useState(false);

  // Mock approved submission data
  const mockSubmission = {
    id: submissionId,
    permitNumber: "PKG2024001",
    address: "123 Oak Street, Jacksonville, FL 32207",
    contractor: { name: "ABC Construction", license: "CBC012345" },
    status: "approved",
    approvedAt: new Date().toISOString(),
    approvalNotes: "All plans meet code requirements",
    files: [
      {
        id: "PRF001-01-00",
        name: "Architectural Plans.pdf",
        documentType: "Architectural",
        needsStamp: true,
        pages: [
          { id: "A1.1", name: "Site Plan", needsStamp: true },
          { id: "A2.1", name: "Floor Plan", needsStamp: true },
          { id: "A3.1", name: "Elevations", needsStamp: true },
        ],
      },
      {
        id: "PRF001-02-00",
        name: "Structural Drawings.pdf",
        documentType: "Structural",
        needsStamp: true,
        pages: [
          { id: "S1.1", name: "Foundation Plan", needsStamp: true },
          { id: "S2.1", name: "Framing Plan", needsStamp: true },
          { id: "S3.1", name: "Details", needsStamp: true },
        ],
      },
      {
        id: "PRF001-03-00",
        name: "Energy Forms.pdf",
        documentType: "Energy Forms",
        needsStamp: false, // NOC and building permit forms don't need stamps
        pages: [],
      },
    ],
    workbench: {
      totalPages: 15,
      compliantPages: 15,
      needsCorrectionPages: 0,
    },
  };

  const steps = [
    "Verify Compliance",
    "Mark Pages for Stamping",
    "Apply Digital Stamps",
    "Generate PCA Form",
    "Finalize Package",
  ];

  useEffect(() => {
    const loadData = () => {
      setSubmission(mockSubmission);

      // Initialize PCA form with user data
      setPcaFormData((prev) => ({
        ...prev,
        reviewedBy:
          `${currentUser?.user?.firstName || ""} ${currentUser?.user?.lastName || ""}`.trim(),
        licenseNumber: currentUser?.user?.licenseNumber || "",
      }));
    };

    loadData();
  }, [submissionId, currentUser]);

  const handlePageCheck = (fileId, pageId, checked) => {
    setCheckedPages((prev) => ({
      ...prev,
      [`${fileId}_${pageId}`]: checked,
    }));
  };

  const handleMarkAllPages = (fileId, checked) => {
    const file = submission.files.find((f) => f.id === fileId);
    if (!file) return;

    const updates = {};
    file.pages.forEach((page) => {
      updates[`${fileId}_${page.id}`] = checked;
    });

    setCheckedPages((prev) => ({ ...prev, ...updates }));
  };

  const getPageCheckCount = (fileId) => {
    const file = submission.files.find((f) => f.id === fileId);
    if (!file) return { checked: 0, total: 0 };

    const checked = file.pages.filter(
      (page) => checkedPages[`${fileId}_${page.id}`]
    ).length;

    return { checked, total: file.pages.length };
  };

  const getTotalStampablePages = () => {
    const stampableFiles = submission.files.filter((f) => f.needsStamp);
    return stampableFiles.reduce((sum, file) => sum + file.pages.length, 0);
  };

  const getTotalCheckedPages = () => {
    const stampableFiles = submission.files.filter((f) => f.needsStamp);
    let checkedCount = 0;

    stampableFiles.forEach((file) => {
      file.pages.forEach((page) => {
        if (checkedPages[`${file.id}_${page.id}`]) {
          checkedCount++;
        }
      });
    });

    return checkedCount;
  };

  const canProceedToStamping = () => {
    return getTotalCheckedPages() === getTotalStampablePages();
  };

  const handleApplyStamps = async () => {
    try {
      setStampingProgress(0);
      const totalPages = getTotalStampablePages();

      // Simulate stamping progress
      for (let i = 1; i <= totalPages; i++) {
        await new Promise((resolve) => setTimeout(resolve, 500));
        setStampingProgress((i / totalPages) * 100);
      }

      setStampingComplete(true);
      setActiveStep(3);
    } catch (error) {
      console.error("Stamping failed:", error);
    }
  };

  const handleGeneratePCA = async () => {
    try {
      // Mock PCA generation
      await new Promise((resolve) => setTimeout(resolve, 1000));
      setPcaGenerated(true);
      setActiveStep(4);
    } catch (error) {
      console.error("PCA generation failed:", error);
    }
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Box>
            <Alert severity="success" sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                ✅ Submission Approved
              </Typography>
              <Typography variant="body2">
                All pages have been reviewed and marked compliant. Ready for
                stamping process.
              </Typography>
            </Alert>

            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="body2" color="text.secondary">
                  Total Pages:
                </Typography>
                <Typography variant="h6">
                  {submission.workbench.totalPages}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="body2" color="text.secondary">
                  Compliant:
                </Typography>
                <Typography variant="h6" color="success.main">
                  {submission.workbench.compliantPages}
                </Typography>
              </Grid>
            </Grid>

            <Button
              variant="contained"
              onClick={() => setActiveStep(1)}
              sx={{ mt: 3 }}
              fullWidth={isMobile}
            >
              Proceed to Stamping
            </Button>
          </Box>
        );

      case 1:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Mark Pages for Stamping
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Select which pages need to be stamped. NOC and building permit
              forms typically don't require stamps.
            </Typography>

            {submission.files.map((file) => (
              <Card key={file.id} sx={{ mb: 2 }}>
                <CardContent>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 2,
                    }}
                  >
                    <Box>
                      <Typography variant="subtitle1" fontWeight="bold">
                        {file.name}
                      </Typography>
                      <Chip
                        label={file.documentType}
                        color="primary"
                        size="small"
                        variant="outlined"
                      />
                      {!file.needsStamp && (
                        <Chip
                          label="No Stamp Required"
                          color="success"
                          size="small"
                          sx={{ ml: 1 }}
                        />
                      )}
                    </Box>

                    {file.needsStamp && (
                      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => handleMarkAllPages(file.id, true)}
                        >
                          Mark All
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => handleMarkAllPages(file.id, false)}
                        >
                          Clear All
                        </Button>
                      </Box>
                    )}
                  </Box>

                  {file.needsStamp && file.pages.length > 0 && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        {getPageCheckCount(file.id).checked} of{" "}
                        {getPageCheckCount(file.id).total} pages selected
                      </Typography>
                      <List dense>
                        {file.pages.map((page) => (
                          <ListItem key={page.id}>
                            <ListItemIcon>
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={
                                      checkedPages[`${file.id}_${page.id}`] ||
                                      false
                                    }
                                    onChange={(e) =>
                                      handlePageCheck(
                                        file.id,
                                        page.id,
                                        e.target.checked
                                      )
                                    }
                                  />
                                }
                                label=""
                              />
                            </ListItemIcon>
                            <ListItemText
                              primary={`${file.documentType} - ${page.name}`}
                              secondary={`Page ID: ${page.id}`}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  )}
                </CardContent>
              </Card>
            ))}

            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2">
                <strong>Summary:</strong> {getTotalCheckedPages()} of{" "}
                {getTotalStampablePages()} stampable pages selected
              </Typography>
            </Alert>

            <Button
              variant="contained"
              onClick={() => setActiveStep(2)}
              disabled={!canProceedToStamping()}
              fullWidth={isMobile}
            >
              Proceed to Stamping
            </Button>
          </Box>
        );

      case 2:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Apply Digital Stamps
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Apply your secure digital stamp and signature to all selected
              pages.
            </Typography>

            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="subtitle2" gutterBottom>
                  Stamping Summary
                </Typography>
                <Typography variant="body2">
                  Pages to stamp: {getTotalCheckedPages()}
                </Typography>
                <Typography variant="body2">
                  Reviewer: {currentUser?.user?.firstName}{" "}
                  {currentUser?.user?.lastName}
                </Typography>
                <Typography variant="body2">
                  License: {currentUser?.user?.licenseNumber || "Not specified"}
                </Typography>
              </CardContent>
            </Card>

            {stampingProgress > 0 && stampingProgress < 100 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" gutterBottom>
                  Stamping Progress: {Math.round(stampingProgress)}%
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={stampingProgress}
                />
              </Box>
            )}

            {!stampingComplete ? (
              <Button
                variant="contained"
                startIcon={<Approval />}
                onClick={handleApplyStamps}
                disabled={stampingProgress > 0 && stampingProgress < 100}
                fullWidth={isMobile}
                size="large"
              >
                Apply Stamps to All Selected Pages
              </Button>
            ) : (
              <Alert severity="success" sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  ✅ Stamping Complete
                </Typography>
                <Typography variant="body2">
                  All selected pages have been digitally stamped and signed.
                </Typography>
              </Alert>
            )}
          </Box>
        );

      case 3:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Generate PCA Form
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Create the Plan Check Approval (PCA) form that will be submitted
              to the city with the stamped documents.
            </Typography>

            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="subtitle2" gutterBottom>
                  PCA Form Information
                </Typography>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="Reviewed By"
                      value={pcaFormData.reviewedBy}
                      onChange={(e) =>
                        setPcaFormData((prev) => ({
                          ...prev,
                          reviewedBy: e.target.value,
                        }))
                      }
                      required
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="PE License Number"
                      value={pcaFormData.licenseNumber}
                      onChange={(e) =>
                        setPcaFormData((prev) => ({
                          ...prev,
                          licenseNumber: e.target.value,
                        }))
                      }
                      required
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="Seal Number"
                      value={pcaFormData.sealNumber}
                      onChange={(e) =>
                        setPcaFormData((prev) => ({
                          ...prev,
                          sealNumber: e.target.value,
                        }))
                      }
                      required
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="Review Date"
                      type="date"
                      value={pcaFormData.reviewDate}
                      onChange={(e) =>
                        setPcaFormData((prev) => ({
                          ...prev,
                          reviewDate: e.target.value,
                        }))
                      }
                      InputLabelProps={{ shrink: true }}
                      required
                    />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      label="Special Conditions/Notes"
                      value={pcaFormData.specialConditions}
                      onChange={(e) =>
                        setPcaFormData((prev) => ({
                          ...prev,
                          specialConditions: e.target.value,
                        }))
                      }
                      placeholder="Any special conditions or notes for the city..."
                    />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={pcaFormData.codeCompliance}
                          onChange={(e) =>
                            setPcaFormData((prev) => ({
                              ...prev,
                              codeCompliance: e.target.checked,
                            }))
                          }
                        />
                      }
                      label="I certify that these plans comply with applicable building codes"
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {!pcaGenerated ? (
              <Button
                variant="contained"
                startIcon={<Description />}
                onClick={handleGeneratePCA}
                disabled={
                  !pcaFormData.reviewedBy ||
                  !pcaFormData.licenseNumber ||
                  !pcaFormData.codeCompliance
                }
                fullWidth={isMobile}
                size="large"
              >
                Generate PCA Form
              </Button>
            ) : (
              <Alert severity="success">
                <Typography variant="h6" gutterBottom>
                  ✅ PCA Form Generated
                </Typography>
                <Typography variant="body2">
                  Plan Check Approval form has been created and is ready for
                  submission to the city.
                </Typography>
              </Alert>
            )}
          </Box>
        );

      case 4:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Package Complete
            </Typography>

            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="subtitle2" gutterBottom>
                  Submission Package Contents
                </Typography>
                <List dense>
                  {submission.files
                    .filter((f) => f.needsStamp)
                    .map((file) => (
                      <ListItem key={file.id}>
                        <ListItemIcon>
                          <CheckCircle color="success" />
                        </ListItemIcon>
                        <ListItemText
                          primary={`${file.name} (Stamped)`}
                          secondary={`${file.pages.length} pages stamped`}
                        />
                      </ListItem>
                    ))}
                  <ListItem>
                    <ListItemIcon>
                      <Description color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary="PCA Form"
                      secondary="Plan Check Approval form"
                    />
                  </ListItem>
                </List>
              </CardContent>
            </Card>

            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Button variant="outlined" startIcon={<Download />} fullWidth>
                  Download Package
                </Button>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Button
                  variant="contained"
                  startIcon={<Send />}
                  fullWidth
                  onClick={() => {
                    // Mark as submitted to city
                    navigate("/pp/submissions");
                  }}
                >
                  Submit to City
                </Button>
              </Grid>
            </Grid>
          </Box>
        );

      default:
        return null;
    }
  };

  if (!submission) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Submission not found or not approved yet.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: isMobile ? 2 : 3, pb: isMobile ? 10 : 3 }}>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
        <IconButton onClick={() => navigate("/pp/submissions")}>
          <ArrowBack />
        </IconButton>
        <Box>
          <Typography color="primary" variant="h4">
            Stamping Workflow
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {submission.permitNumber} - {submission.address}
          </Typography>
        </Box>
      </Box>

      {/* Progress Stepper */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stepper
            activeStep={activeStep}
            orientation={isMobile ? "vertical" : "horizontal"}
            sx={{ mb: 3 }}
          >
            {steps.map((label, index) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
                {isMobile && (
                  <StepContent>
                    {index === activeStep && renderStepContent()}
                  </StepContent>
                )}
              </Step>
            ))}
          </Stepper>

          {!isMobile && renderStepContent()}
        </CardContent>
      </Card>

      {/* Quick Stats (Mobile) */}
      {isMobile && activeStep === 1 && (
        <Paper sx={{ p: 2, mb: 2, bgcolor: "primary.light" }}>
          <Typography variant="body2" color="primary.contrastText">
            <strong>Progress:</strong> {getTotalCheckedPages()} of{" "}
            {getTotalStampablePages()} pages marked
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default PpStampingWorkflowPage;
